"use client";

import React from "react";
import { formatRelativeTime } from "utils/formatRelativeTime";
import {
  type AutocompleteGameCard,
  FinishEnum,
  NONFOIL_FINISH,
} from "modules/cards/model/card";
import { EmbeddedCardPrice } from "modules/cards/model/card-price";
import FoilIndicator from "modules/common/components/Table/FoilIndicator";
import { cn, getFinishPrice } from "utils/utils";
import { formatCurrency } from "utils/currency";
import { useHydrated } from "modules/common/hooks/useHydrated";
import {
  finishSpec,
  priceFinishes,
  priceForFinish,
} from "modules/cards/model/finishBehavior";

type AutocompleteCardPrice = AutocompleteGameCard["price"];
type CardLike = {
  finishes: FinishEnum[];
  price?: EmbeddedCardPrice | AutocompleteCardPrice;
};

interface CardFinishPricesProps {
  card: CardLike;
  finish?: FinishEnum;
  className?: string;
  emptyFallback?: React.ReactNode;
}

// Canonical finish display order, shared so price rows and finish controls
// elsewhere (e.g. the change-printing buttons) stay in the same order.
// Orders a card's finishes by the canonical order, appending any finish not in
// that list (e.g. glossy/signed) so callers that render one control per finish
// never silently drop one.
export function orderedFinishes(finishes: FinishEnum[]): FinishEnum[] {
  const displayOrder = priceFinishes();
  const known = displayOrder.filter((f) => finishes.includes(f));
  const rest = finishes.filter((f) => !displayOrder.includes(f));
  return [...known, ...rest];
}

function finishTimestampISO(
  price: EmbeddedCardPrice | AutocompleteCardPrice | undefined,
  finish: FinishEnum
): string | undefined {
  if (!price || !("value" in price)) return undefined;
  const key = finishSpec(finish).timestampKey;
  if (!key) return undefined;
  const value = price[key];
  return typeof value === "string" ? value : undefined;
}

function finishPrice(
  price: EmbeddedCardPrice | AutocompleteCardPrice | undefined,
  finish: FinishEnum
): string {
  if (!price) return "";
  if ("value" in price) return getFinishPrice(price, finish);

  const value = priceForFinish(price, finish);
  return value > 0 ? formatCurrency(value, price.currency) : "";
}

/**
 * Renders per-finish prices for both embedded catalogue cards and the flatter
 * autocomplete result shape. Passing `finish` narrows the output to that exact
 * finish; otherwise every finish with a real price is rendered, separated by
 * `/` with the foil/etched indicator beside non-default finishes. Embedded
 * prices also expose their per-finish freshness timestamp; autocomplete prices
 * do not carry one.
 */
export function CardFinishPrices({
  card,
  finish,
  className,
  emptyFallback = null,
}: CardFinishPricesProps) {
  // Freshness lands in a `data-tip` attribute, which suppressHydrationWarning
  // can't cover — withhold it until after mount so server and client agree.
  const hydrated = useHydrated();
  const displayedFinishes = finish
    ? card.finishes.filter((value) => value === finish)
    : card.finishes;
  const entries = orderedFinishes(displayedFinishes)
    .map((f) => ({
      finish: f,
      label: finishPrice(card.price, f),
      timestamp: finishTimestampISO(card.price, f),
    }))
    .filter((e) => e.label !== "");

  if (entries.length === 0) return <>{emptyFallback}</>;

  return (
    <span className={cn("flex items-center gap-1", className)}>
      {entries.map((e, i) => {
        const tip =
          hydrated && e.timestamp
            ? `Updated ${formatRelativeTime(e.timestamp)}`
            : undefined;
        return (
          <React.Fragment key={e.finish}>
            {i > 0 && <span>/</span>}
            <span
              className={cn("flex items-center", tip && "tooltip tooltip-top")}
              data-tip={tip}
            >
              {finishSpec(e.finish).foilVisual && <FoilIndicator />}
              {e.finish !== NONFOIL_FINISH &&
                !finishSpec(e.finish).foilVisual && (
                  <span className="badge badge-xs badge-outline mr-0.5">
                    {finishSpec(e.finish).label}
                  </span>
                )}
              {e.label}
            </span>
          </React.Fragment>
        );
      })}
    </span>
  );
}
